// 1. Dependencies
import { nodeConfig } from '@scripts/mixins';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import debounce from 'lodash/debounce';

gsap.registerPlugin(ScrollTrigger);

// 2. Configuration
const NAMESPACE = 'hero';
const DEFAULT_SELECTOR = '[data-js-hero]';
const DEFAULT_SETTINGS = {
};

// 3. Module
class Hero {
	/** @param {HTMLElement} node */
	constructor(node, config) {
		this.elNode = node;
		this.elBackground = this.elNode.querySelector('[data-js-hero-background]');
		this.elLogo = this.elNode.querySelector('[data-js-hero-logo]');
		this.elLogoIconWrapper = this.elNode.querySelector('[data-js-hero-logo-icon-wrapper]');
		this.elLogoIcon = this.elNode.querySelector('[data-js-hero-logo-icon]');
		this.elTaglineWrapper = this.elNode.querySelector('[data-js-hero-tagline-wrapper]');
		this.elTagline = this.elNode.querySelector('[data-js-hero-tagline]');
		this.elCtaWrapper = this.elNode.querySelector('[data-js-hero-cta-wrapper]');
		this.elCta = this.elNode.querySelector('[data-js-hero-cta]');
		this.elHeader = document.querySelector('[data-js-header]');
		this.elHeaderLogoWrapper = document.querySelector('[data-js-header-logo]');
		this.elHeaderLogo = this.elHeaderLogoWrapper?.querySelector('.icon');

		this.config = config;

		this.gsapMm = gsap.matchMedia();
		this.scrollAnimation = null;
		this.entranceAnimation = null;
		this.rootFontSize = null;
	}

	setLogoTransitionTargets() {
		const logoRect = this.elLogo.getBoundingClientRect();
		const headerLogoRect = this.elHeaderLogo.getBoundingClientRect();
		const logoCenter = logoRect.left + logoRect.width / 2;
		const headerLogoCenter = headerLogoRect.left + headerLogoRect.width / 2;

		gsap.set(this.elNode, {
			'--logo-scale-target': headerLogoRect.width / logoRect.width,
			'--logo-translate-target': headerLogoCenter - logoCenter,
		});

		ScrollTrigger.refresh();
	}

	bindUI() {
		this.setLogoTransitionTargets();

		this.gsapMm.add('(min-width: 76.25em)', () => {
			this.entranceAnimation = gsap.timeline({
				defaults: {
					ease: 'sine.out',
				}
			})
				.to(this.elBackground, {
					scale: 1,
					duration: 1.25,
				})
				.to(this.elLogoIconWrapper, {
					opacity: 1,
					scale: 1,
					duration: 1.25,
				}, '<');

			if (this.elTaglineWrapper) {
				this.entranceAnimation.to(this.elTaglineWrapper, {
					opacity: 1,
					y: 0,
					duration: 1,
				}, 0.25);
			}

			if (this.elCtaWrapper) {
				this.entranceAnimation.to(this.elCtaWrapper, {
					opacity: 1,
					y: 0,
					duration: 1,
				}, 0.25);
			}

			this.scrollAnimation = gsap.timeline({
				defaults: {
					ease: 'sine.out',
				},
				scrollTrigger: {
					trigger: 'body',
					start: 'top top',
					endTrigger: this.elLogo,
					end: () => {
						const stickyOffset = getComputedStyle(this.elHeader).getPropertyValue('--header-sticky-shift').trim();
						const stickyOffsetPx = stickyOffset.endsWith('rem')
							? parseFloat(stickyOffset) * this.rootFontSize
							: parseFloat(stickyOffset);

						const headerLogoRect = this.elHeaderLogo.getBoundingClientRect();
						const headerRect = this.elHeader.getBoundingClientRect();
						const headerLogoOffsetTop = headerLogoRect.top - headerRect.top;
						const logoCenterFromTop = headerLogoOffsetTop - stickyOffsetPx + headerLogoRect.height / 2;

						return `50% ${logoCenterFromTop}px`;
					},
					scrub: true,
					onLeave: () => {
						this.elNode.classList.add('hero--scroll-complete');
					},
					onEnterBack: () => {
						this.elNode.classList.remove('hero--scroll-complete');
					},
				}
			})
				.to(this.elLogoIcon, {
					'--animation-progress': 1,
				});

			if (this.elTagline) {
				this.scrollAnimation.to(this.elTagline, { opacity: 0, y: 150 }, 0);
			}

			if (this.elCta) {
				this.scrollAnimation.to(this.elCta, { opacity: 0, y: 150 }, 0);
			}
		});

		window.addEventListener('resize', debounce(this.setLogoTransitionTargets.bind(this), 100));
	}

	init() {
		if (window.clearHeroStartingStyles) {
			clearTimeout(window.clearHeroStartingStyles);
		}

		if (this.elNode.classList.contains('hero--no-js')) {
			return;
		}

		this.rootFontSize = parseFloat(getComputedStyle(document.documentElement).fontSize);

		this.bindUI();
	}
}

// 4. Factory Function
export default function factory(selector = DEFAULT_SELECTOR, settings = {}) {
	const listNode = document.querySelectorAll(selector);

	if (!listNode.length) return;

	return Array.from(listNode).map((node) => {
		const config = nodeConfig(node, NAMESPACE, settings, DEFAULT_SETTINGS);
		const module = new Hero(node, config);

		module.init();

		return module;
	});
}
